import React, { useEffect, useState } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { useForm, FormProvider } from "react-hook-form";
import { Highlighter, Typeahead } from "react-bootstrap-typeahead";
import PropTypes from "prop-types";

import { getScacCodes, submitTrackingReferences, updateBookingReference, updateTrackingReferences } from "api";
import { useGetTrackingReferences } from "api/queries/shipments/useGetTrackingReferences";
import Button from "components/atoms/Button/Button";
import Dialog from "components/organisms/Dialog/Dialog";
import validateTrackingNumber from "helpers/trackingNumberValidator";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import testIds from "constants/testIds";
import errorToast from "helpers/errorToast";
import Checkbox from "components/atoms/Checkbox/Checkbox";
import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";
import getAllowedTrackingTypes from "pages/common/ShipmentTrackingV2/components/Tracking/getAllowedTrackingTypes";
import InputField from "components/atoms/InputField/InputField";
import { ReactComponent as InfoIcon } from "assets/images/icons/Info.svg";
import TrackingStopAssociationField from "./TrackingStopAssociationField";
import { useTrackingStopAssociation } from "./useTrackingStopAssociation";

const ReferencesDialog = function ({
  isOpen,
  close,
  typeOptions,
  prevReferences,
  refreshTrackingData,
  shipmentId,
  referenceId,
  isBookingReference,
  bookingReference,
  freightMode,
  freightModeType,
  company_shipper_settings,
}) {
  if (!isOpen) return null;
  const intl = useIntl();
  const methods = useForm();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [type, setType] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [typeInformation, setTypeInformation] = useState(null);
  const [options, setOptions] = useState(typeOptions);
  const [scacCode, setScacCode] = useState([]);
  const [scacOtherCode, setScacOtherCode] = useState("");
  const [validationError, setValidationError] = useState(null);
  const [validationErrorMessage, setValidationErrorMessage] = useState("");
  const [scacCodes, setScacCodes] = useState([]);
  const [scacOptions, setScacOptions] = useState([]);
  const [associatedStopId, setAssociatedStopId] = useState(null);

  const { isEnabled: canAssociateStop, stops: deliveryStops } = useTrackingStopAssociation(shipmentId);

  const { data: trackingReferencesData } = useGetTrackingReferences(shipmentId, {
    enabled: !!shipmentId,
    refetchOnMount: false,
  });
  const { references = [] } = trackingReferencesData ?? {};

  const unsupportedLiveTrackingTypes = ["rail_car"];

  // Get SCAC codes from API and set the scacOptions state
  useEffect(() => {
    getScacCodes()
      .then((res) => {
        const scacCodes = res.data;
        setScacCodes(scacCodes);
        const newOptions = scacCodes.map(({ number, name }, idx) => ({
          id: `${idx}-${number}`,
          value: name,
          label: number,
        }));
        setScacOptions((prevOptions) => [...prevOptions, ...newOptions]);
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "general.couldNotRetrieveScacCodes",
            defaultMessage: "Could not retrieve SCAC codes. Please try again later.",
          }),
        ),
      );
  }, []);

  useEffect(() => {
    // If referenceId is present, pre-populate the form with the existing reference
    if (referenceId) {
      const reference = prevReferences.find((r) => r.id === referenceId);
      setAssociatedStopId(reference?.stop?.id ?? null);
      if (reference.type) {
        setType(typeOptions.find((t) => t.value === reference.type));
        setTypeInformation(validateTrackingNumber(reference.type));
        // if reference is a mbol, split the reference value into scac code and tracking number
        if (reference.type === "mbol") {
          const refSplit = reference.tracking_number.split("||");
          const scacPrefix = refSplit[0];
          const scacTrackingNumber = refSplit[1];
          const scacOption = scacOptions.find((s) => s.label === scacPrefix);
          setTrackingNumber(scacTrackingNumber);
          if (scacOption) setScacCode([{ label: scacOption.label, value: scacOption.value }]);
          else {
            setScacCode([{ label: "other", value: "Other" }]);
            setScacOtherCode(scacPrefix);
          }
        } else if (reference.type === "container_no") {
          setTrackingNumber(reference.tracking_number);
          if (reference.scac) {
            methods.setValue("scacEnabled", true);
            methods.setValue("scac", reference.scac);
          }
          if (reference.bol) {
            methods.setValue("bookingEnabled", true);
            methods.setValue("bol", reference.bol);
          }
        } else setTrackingNumber(reference.tracking_number); // Otherwise, just set the tracking number
      }
    } else {
      // The dialog stays mounted between opens, so drop any stop kept from a previous edit
      setAssociatedStopId(null);
      if (isBookingReference) {
        // If referenceId is not present, but the reference is a booking reference, pre-populate the form with the booking reference
        setType(typeOptions.find((t) => t.value === "bookingReference"));
        setTrackingNumber(bookingReference);
      }
    }
  }, [referenceId]);

  useEffect(() => {
    const primaryReference = prevReferences?.find((r) => r.type === "primary");
    // Remove primary and booking reference options if they already exist (they can only be added once)
    let newOptions = getAllowedTrackingTypes(freightMode, freightModeType, typeOptions);
    if (primaryReference) newOptions = newOptions.filter((t) => t.value !== "primary");
    if (bookingReference) newOptions = newOptions.filter((t) => t.value !== "bookingReference");
    setOptions(newOptions);
  }, [typeOptions, freightMode, freightModeType]);

  useEffect(() => {
    setValidationError(false);
    setValidationErrorMessage("");
    setTypeInformation(validateTrackingNumber(type.value));
    // setScacCode("");
  }, [type]);

  const onTrackingNumberChange = (e) => {
    let { value } = e.target;
    if (["rail_car", "container_no"].includes(type?.value)) {
      value = value.toUpperCase();
    }
    setTrackingNumber(value);
  };
  const onTypeSelect = (selectedType) => setType(selectedType);
  const onSave = async () => {
    if (validationError) return;
    if (type.value === "container_no") {
      const isValid = await methods.trigger();
      if (!isValid) {
        return;
      }
    }
    setIsSubmitting(true);

    // If the reference being edited or added is a booking reference, update the booking reference(PUT)
    if (isBookingReference || type.value === "bookingReference") {
      const newBookingReference = {
        booking_reference_value: trackingNumber,
      };
      updateBookingReference(shipmentId, newBookingReference)
        .then(() => {
          refreshTrackingData(type?.value);
          close();
        })
        .catch((err) => errorToast(err));
    } else if (referenceId) {
      // If referenceId is present, update the reference (PUT)
      const updatedReference = {
        references: [
          {
            id: referenceId,
            type: type.value,
            tracking_number: generateReferenceValue(),
            ...(methods.getValues("scacEnabled") &&
              type.value === "container_no" && {
                scac: methods.getValues("scac"),
              }),
            ...(methods.getValues("bookingEnabled") &&
              type.value === "container_no" && {
                bol: methods.getValues("bol"),
              }),
            ...(canAssociateStop && { stop_id: associatedStopId }),
          },
        ],
      };

      updateTrackingReferences(shipmentId, updatedReference)
        .then(() => {
          refreshTrackingData(type?.value);
          close();
        })
        .catch((err) => errorToast(err))
        .finally(() => setIsSubmitting(false));
    } else {
      // Otherwise, create a new reference (POST)
      const newReference = {
        references: [
          {
            type: type.value,
            tracking_number: generateReferenceValue(),
            ...(methods.getValues("scacEnabled") &&
              type.value === "container_no" && {
                scac: methods.getValues("scac"),
              }),
            ...(methods.getValues("bookingEnabled") &&
              type.value === "container_no" && {
                bol: methods.getValues("bol"),
              }),
            ...(canAssociateStop && { stop_id: associatedStopId }),
          },
        ],
      };
      submitTrackingReferences(shipmentId, newReference)
        .then(() => {
          refreshTrackingData(type?.value);
          close();
        })
        .catch((err) => errorToast(err))
        .finally(() => setIsSubmitting(false));
    }
  };

  const onTrackingNumberPaste = (e) => {
    const isScac = type.value === "mbol";
    const pastedValue = e.clipboardData.getData("text");
    if (pastedValue.length < 4 || !isScac) return;

    const firstFour = pastedValue.slice(0, 4);
    if (isNaN(firstFour)) {
      const pastedCodeData = scacCodes.find((item) => item.number.toLowerCase() === firstFour.toLowerCase());
      if (pastedCodeData) {
        setScacCode([{ label: pastedCodeData.number, value: pastedCodeData.name }]);
      } else {
        setScacCode([{ label: "other", value: "Other" }]);
        setScacOtherCode(firstFour);
      }
      setTimeout(() => {
        setTrackingNumber(pastedValue.slice(4));
      }, 0);
    }
  };

  useEffect(() => validateInput(), [trackingNumber]);

  const isButtonDisabled = () => {
    if (referenceId && !trackingNumber) return false;
    const isScacCodeEmpty = type.value === "mbol" && scacCode.length === 0;
    const isScacOtherCodeEmpty = scacCode.length > 0 && scacCode[0].label === "other" && scacOtherCode.length < 4;
    return !type || validationError || !trackingNumber || isScacCodeEmpty || isScacOtherCodeEmpty;
  };

  const validateInput = () => {
    const duplicatedReference = references.find((ref) => ref.tracking_number === trackingNumber);
    if (duplicatedReference) {
      setValidationError(true);
      if (duplicatedReference.type === "container_no") setValidationErrorMessage("Container number already added.");
      else if (duplicatedReference.type === "mawb") setValidationErrorMessage("MAWB number already added.");
      else setValidationErrorMessage("Tracking number already added.");
      return;
    }

    setValidationErrorMessage(undefined);

    if (!typeInformation || !typeInformation.validationPattern) {
      setValidationError(false);
      return;
    }

    const regex = new RegExp(typeInformation.validationPattern);
    if (trackingNumber && !regex.test(trackingNumber)) {
      setValidationError(true);
      return;
    }

    setValidationError(false);
  };

  // If the type is mbol, the value is a combination of the scac code and the tracking number
  const generateReferenceValue = () => {
    let newReferenceValue = trackingNumber;
    if (trackingNumber) {
      if (scacCode && type.value === "mbol") {
        if (scacCode[0].label === "other") newReferenceValue = `${scacOtherCode}||${trackingNumber}`;
        else newReferenceValue = `${scacCode[0].label}||${trackingNumber}`;
      }
    }
    return newReferenceValue;
  };

  return (
    <Dialog
      show={isOpen}
      onHide={close}
      title={
        !referenceId
          ? intl.formatMessage({
              id: "tracking.reference",
              defaultMessage: "Reference",
            })
          : "Edit reference"
      }
      size="sm"
    >
      <div className="d-flex flex-column" style={{ zIndex: 999 }}>
        {/** TRACKING TYPE DROPDOWN/LABEL */}
        {referenceId || isBookingReference ? (
          <p style={{ marginBottom: -10 }}>{type.label}</p>
        ) : (
          <Dropdown options={options} onChange={onTypeSelect} defaultValue="Please select" />
        )}
        {/** TRACKING NUMBER INPUT */}
        {type.value && type.value !== "mbol" && (
          <>
            {["container_no", "truck"].includes(type.value) &&
              (type.value === "container_no" ? (
                <p className="mt-3 mb-2 font-b3">
                  <FormattedMessage id="tableHeaders.containerNumber" defaultMessage="Container number" />
                </p>
              ) : company_shipper_settings?.truck_tracking_enabled ? (
                <p className="mt-3 mb-2 font-b3">
                  <FormattedMessage
                    id="tracking.referenceOptionTruckLicensePlateNumber"
                    defaultMessage="Truck license plate number"
                  />
                </p>
              ) : (
                <p className="mt-3 mb-2" />
              ))}
            <input
              type="text"
              value={trackingNumber}
              onChange={onTrackingNumberChange}
              placeholder={typeInformation ? typeInformation.placeholder : "Please provide"}
              className={`rounded-input ${["container_no", "truck"].includes(type.value) ? "" : "mt-4"}`}
              maxLength={30}
              style={{ border: validationError ? "1px solid red" : "" }}
            />
            {validationErrorMessage && <div className="text-danger font-b3 mt-xxs">{validationErrorMessage}</div>}
          </>
        )}
        {/** MBOL/CONTAINER INPUT */}
        {type.value && type.value === "mbol" && (
          <div className="d-flex" style={{ gap: 8, marginTop: referenceId ? 10 : 0 }}>
            <div className="flex-column">
              <p className="mt-3 mb-2 font-b3">SCAC code</p>
              <Typeahead
                onChange={setScacCode}
                inputProps={{
                  style: { height: 32 },
                  className: "rounded-input",
                }}
                selected={scacCode}
                options={scacOptions}
                renderMenuItemChildren={({ label, value }, { text }) => (
                  <div className="p-1">
                    <div className="font-weight-bold font-b3 mb-1">
                      <Highlighter search={text}>{`${label}`}</Highlighter>
                    </div>
                    <div className="font-12">
                      <Highlighter search={text}>{value}</Highlighter>
                    </div>
                  </div>
                )}
                filterBy={["label", "value"]}
                placeholder="i.e. ABCD"
                id="scac_input"
              />
            </div>
            <div className="flex-column">
              <p className="mt-3 mb-2 font-b3">
                <FormattedMessage id="quote-request.trackingNumber" defaultMessage="Tracking number" />
              </p>
              <div className="d-flex">
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={onTrackingNumberChange}
                  onPaste={onTrackingNumberPaste}
                  placeholder={typeInformation ? typeInformation.placeholder : "Please provide"}
                  className="rounded-input"
                />

                {scacCode[0] && scacCode[0].label === "other" && (
                  <input
                    type="text"
                    value={scacOtherCode}
                    onChange={(e) => setScacOtherCode(e.target.value.toUpperCase())} // Convert input to uppercase
                    placeholder="ABCD"
                    maxLength={4}
                    className="rounded-input ml-2"
                    style={{ width: 55 }}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/** TRACKING NUMBER VALIDATION INFO */}
        {typeInformation && typeInformation.infoMessage && (
          <div className="font-b5 text-secondary" style={{ maxWidth: 250, color: validationError ? "#EA432D" : "" }}>
            {typeInformation.infoMessage}
          </div>
        )}
        {type.value && type.value === "container_no" && (
          <FormProvider {...methods}>
            <div>
              <p className="text-secondary font-b6">Additional reference numbers</p>
              <p className="font-b3 mt-1">
                More reference numbers will allow <a href="_">for more accurate tracking.</a>{" "}
              </p>
              <div className="mt-3">
                <Checkbox
                  providerSections
                  checkboxLabel="SCAC"
                  name="scacEnabled"
                  validations={{
                    shouldUnregister: true,
                  }}
                />
                {methods.watch("scacEnabled") && (
                  <div className="flex-row">
                    <div className="flex-1">
                      <SearchSelect
                        enableManualValue
                        filterBy={["label"]}
                        name="scac"
                        shouldUnregister
                        options={scacOptions}
                        classes="mt-3"
                        placeholder="Select"
                        required
                        validations={{
                          validate: {
                            mustBe4Characters: (value) => value.length === 4,
                          },
                        }}
                        onInputChange={(value) => methods.setValue("scac", value.toUpperCase())} // Convert input to uppercase
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-3">
                <Checkbox
                  providerSections
                  checkboxLabel="BL / Booking number"
                  name="bookingEnabled"
                  validations={{
                    shouldUnregister: true,
                  }}
                />
                {methods.watch("bookingEnabled") && (
                  <div className="mt-3 flex-row">
                    <div className="flex-1">
                      <InputField
                        field_id="bol"
                        required
                        validations={{
                          shouldUnregister: true,
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </FormProvider>
        )}
        {unsupportedLiveTrackingTypes.includes(type.value) && (
          <div className="d-flex font-b5 text-secondary mt-4" style={{ maxWidth: 250 }}>
            <InfoIcon className="mr-2" width={20} height={20} />
            <div>
              <FormattedMessage
                id="tracking.doNotSupportThisReferenceType"
                defaultMessage="We currently do not support live tracking of this reference type."
              />
            </div>
          </div>
        )}
        {type.value && type.value === "truck" && company_shipper_settings?.truck_tracking_enabled && (
          <div className="text-warning-primary font-b3 mt-md" style={{ maxWidth: 250 }}>
            <FormattedMessage
              id="tracking.truckHelpMessage"
              defaultMessage="We will generate a link for you to send to the truck driver to track their location."
            />
          </div>
        )}
        {canAssociateStop && !isBookingReference && type.value !== "bookingReference" && (
          <TrackingStopAssociationField stops={deliveryStops} value={associatedStopId} onChange={setAssociatedStopId} />
        )}
      </div>
      <div className="d-flex" style={{ marginTop: 32, zIndex: 1 }}>
        <Button
          variant="outline-primary"
          size="lg"
          label={intl.formatMessage({
            id: "general.cancel",
            defaultMessage: "Cancel",
          })}
          onClick={() => {
            close();
          }}
        />
        <Button
          className="ml-auto"
          variant="primary"
          size="lg"
          isLoading={isSubmitting}
          label={intl.formatMessage({
            id: "general.save",
            defaultMessage: "Save",
          })}
          type="submit"
          onClick={onSave}
          disabled={isButtonDisabled()}
          data-testid={testIds.SHIPMENT_DASHBOARD_TRACKING_REFERENCE_SUBMIT_BUTTON}
        />
      </div>
    </Dialog>
  );
};

ReferencesDialog.propTypes = {
  freightMode: PropTypes.string,
  freightModeType: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  typeOptions: PropTypes.instanceOf(Array).isRequired,
  prevReferences: PropTypes.instanceOf(Array).isRequired,
  shipmentId: PropTypes.string.isRequired,
  refreshTrackingData: PropTypes.func.isRequired,
  referenceId: PropTypes.number,
  isBookingReference: PropTypes.bool.isRequired,
  bookingReference: PropTypes.string,
  company_shipper_settings: PropTypes.instanceOf(Object),
};

ReferencesDialog.defaultProps = {
  freightMode: "",
  freightModeType: "",
  referenceId: null,
  bookingReference: null,
  company_shipper_settings: {},
};

export default ReferencesDialog;
